export interface ErrorInfo {
  code: string;
  category: ErrorCategory;
  userMessage: string;
  technicalMessage: string;
  actionable: boolean;
  retryable: boolean;
  retryCount?: number;
  maxRetries?: number;
}

export enum ErrorCategory {
  NETWORK = 'network',
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  VALIDATION = 'validation',
  NOT_FOUND = 'not_found',
  CONFLICT = 'conflict',
  SERVER_ERROR = 'server_error',
  TIMEOUT = 'timeout',
  UNKNOWN = 'unknown'
}

export interface RetryConfig {
  maxRetries: number;
  baseDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
}

export class ErrorHandler {
  private static readonly DEFAULT_RETRY_CONFIG: RetryConfig = {
    maxRetries: 3,
    baseDelay: 1000,
    maxDelay: 10000,
    backoffMultiplier: 2
  };

  private static readonly ERROR_MAP: Map<string, ErrorInfo> = new Map([
    // Network Errors
    ['NETWORK_ERROR', {
      code: 'NETWORK_ERROR',
      category: ErrorCategory.NETWORK,
      userMessage: 'Unable to connect to the server. Please check your internet connection and try again.',
      technicalMessage: 'Network connection failed',
      actionable: true,
      retryable: true,
      maxRetries: 3
    }],
    ['TIMEOUT_ERROR', {
      code: 'TIMEOUT_ERROR',
      category: ErrorCategory.TIMEOUT,
      userMessage: 'The request took too long to complete. Please try again.',
      technicalMessage: 'Request timeout',
      actionable: true,
      retryable: true,
      maxRetries: 2
    }],

    // Authentication Errors
    ['UNAUTHORIZED', {
      code: 'UNAUTHORIZED',
      category: ErrorCategory.AUTHENTICATION,
      userMessage: 'Please log in to continue.',
      technicalMessage: 'Authentication required',
      actionable: true,
      retryable: false
    }],
    ['TOKEN_EXPIRED', {
      code: 'TOKEN_EXPIRED',
      category: ErrorCategory.AUTHENTICATION,
      userMessage: 'Your session has expired. Please log in again.',
      technicalMessage: 'JWT token expired',
      actionable: true,
      retryable: false
    }],

    // Authorization Errors
    ['FORBIDDEN', {
      code: 'FORBIDDEN',
      category: ErrorCategory.AUTHORIZATION,
      userMessage: 'You don\'t have permission to perform this action.',
      technicalMessage: 'Insufficient permissions',
      actionable: true,
      retryable: false
    }],

    // Validation Errors
    ['VALIDATION_ERROR', {
      code: 'VALIDATION_ERROR',
      category: ErrorCategory.VALIDATION,
      userMessage: 'Please check your input and try again.',
      technicalMessage: 'Input validation failed',
      actionable: true,
      retryable: false
    }],
    ['INVALID_DATE', {
      code: 'INVALID_DATE',
      category: ErrorCategory.VALIDATION,
      userMessage: 'Please select a valid date and time.',
      technicalMessage: 'Invalid date format',
      actionable: true,
      retryable: false
    }],

    // Not Found Errors
    ['NOT_FOUND', {
      code: 'NOT_FOUND',
      category: ErrorCategory.NOT_FOUND,
      userMessage: 'The requested resource was not found.',
      technicalMessage: 'Resource not found',
      actionable: false,
      retryable: false
    }],
    ['PROFESSIONAL_NOT_FOUND', {
      code: 'PROFESSIONAL_NOT_FOUND',
      category: ErrorCategory.NOT_FOUND,
      userMessage: 'The selected professional is no longer available.',
      technicalMessage: 'Professional not found',
      actionable: true,
      retryable: false
    }],
    ['SERVICE_NOT_FOUND', {
      code: 'SERVICE_NOT_FOUND',
      category: ErrorCategory.NOT_FOUND,
      userMessage: 'The selected service is no longer available.',
      technicalMessage: 'Service not found',
      actionable: true,
      retryable: false
    }],

    // Conflict Errors
    ['APPOINTMENT_CONFLICT', {
      code: 'APPOINTMENT_CONFLICT',
      category: ErrorCategory.CONFLICT,
      userMessage: 'This time slot is no longer available. Please select another time.',
      technicalMessage: 'Appointment time conflict',
      actionable: true,
      retryable: false
    }],
    ['SCHEDULE_CONFLICT', {
      code: 'SCHEDULE_CONFLICT',
      category: ErrorCategory.CONFLICT,
      userMessage: 'This schedule conflicts with existing appointments.',
      technicalMessage: 'Schedule conflict detected',
      actionable: true,
      retryable: false
    }],

    // Server Errors
    ['INTERNAL_SERVER_ERROR', {
      code: 'INTERNAL_SERVER_ERROR',
      category: ErrorCategory.SERVER_ERROR,
      userMessage: 'Something went wrong on our end. Please try again later.',
      technicalMessage: 'Internal server error',
      actionable: true,
      retryable: true,
      maxRetries: 2
    }],
    ['SERVICE_UNAVAILABLE', {
      code: 'SERVICE_UNAVAILABLE',
      category: ErrorCategory.SERVER_ERROR,
      userMessage: 'The service is temporarily unavailable. Please try again later.',
      technicalMessage: 'Service unavailable',
      actionable: true,
      retryable: true,
      maxRetries: 3
    }],

    // Unknown Errors
    ['UNKNOWN_ERROR', {
      code: 'UNKNOWN_ERROR',
      category: ErrorCategory.UNKNOWN,
      userMessage: 'An unexpected error occurred. Please try again.',
      technicalMessage: 'Unknown error',
      actionable: true,
      retryable: true,
      maxRetries: 1
    }]
  ]);

  /**
   * Categorize and format an error
   */
  static categorizeError(error: any): ErrorInfo {
    // Handle Axios errors
    if (error.isAxiosError) {
      return this.handleAxiosError(error);
    }

    // Handle network errors
    if (error.message?.includes('Network Error') || error.message?.includes('fetch')) {
      return this.ERROR_MAP.get('NETWORK_ERROR')!;
    }

    // Handle timeout errors
    if (error.message?.includes('timeout') || error.code === 'ECONNABORTED') {
      return this.ERROR_MAP.get('TIMEOUT_ERROR')!;
    }

    // Handle specific error codes
    if (error.code && this.ERROR_MAP.has(error.code)) {
      return this.ERROR_MAP.get(error.code)!;
    }

    // Default to unknown error
    return this.ERROR_MAP.get('UNKNOWN_ERROR')!;
  }

  /**
   * Handle Axios-specific errors
   */
  private static handleAxiosError(error: any): ErrorInfo {
    const status = error.response?.status;
    const data = error.response?.data;

    switch (status) {
      case 400:
        if (data?.message?.includes('validation')) {
          return this.ERROR_MAP.get('VALIDATION_ERROR')!;
        }
        return this.ERROR_MAP.get('VALIDATION_ERROR')!;

      case 401:
        if (data?.message?.includes('expired') || data?.message?.includes('token')) {
          return this.ERROR_MAP.get('TOKEN_EXPIRED')!;
        }
        return this.ERROR_MAP.get('UNAUTHORIZED')!;

      case 403:
        return this.ERROR_MAP.get('FORBIDDEN')!;

      case 404:
        if (data?.message?.includes('professional')) {
          return this.ERROR_MAP.get('PROFESSIONAL_NOT_FOUND')!;
        }
        if (data?.message?.includes('service')) {
          return this.ERROR_MAP.get('SERVICE_NOT_FOUND')!;
        }
        return this.ERROR_MAP.get('NOT_FOUND')!;

      case 409:
        if (data?.message?.includes('conflict') || data?.message?.includes('appointment')) {
          return this.ERROR_MAP.get('APPOINTMENT_CONFLICT')!;
        }
        if (data?.message?.includes('schedule')) {
          return this.ERROR_MAP.get('SCHEDULE_CONFLICT')!;
        }
        return this.ERROR_MAP.get('CONFLICT')!;

      case 500:
        return this.ERROR_MAP.get('INTERNAL_SERVER_ERROR')!;

      case 503:
        return this.ERROR_MAP.get('SERVICE_UNAVAILABLE')!;

      default:
        return this.ERROR_MAP.get('UNKNOWN_ERROR')!;
    }
  }

  /**
   * Calculate retry delay with exponential backoff
   */
  static calculateRetryDelay(retryCount: number, config: Partial<RetryConfig> = {}): number {
    const finalConfig = { ...this.DEFAULT_RETRY_CONFIG, ...config };
    const delay = finalConfig.baseDelay * Math.pow(finalConfig.backoffMultiplier, retryCount);
    return Math.min(delay, finalConfig.maxDelay);
  }

  /**
   * Check if an error should be retried
   */
  static shouldRetry(error: any, retryCount: number, config: Partial<RetryConfig> = {}): boolean {
    const errorInfo = this.categorizeError(error);
    const finalConfig = { ...this.DEFAULT_RETRY_CONFIG, ...config };

    if (!errorInfo.retryable) return false;
    if (retryCount >= finalConfig.maxRetries) return false;

    return true;
  }

  /**
   * Get retry configuration for an error
   */
  static getRetryConfig(error: any): RetryConfig {
    const errorInfo = this.categorizeError(error);
    return {
      ...this.DEFAULT_RETRY_CONFIG,
      maxRetries: errorInfo.maxRetries || this.DEFAULT_RETRY_CONFIG.maxRetries
    };
  }

  /**
   * Log error for debugging
   */
  static logError(error: any, context?: string): void {
    const errorInfo = this.categorizeError(error);
    
    console.error(`[${errorInfo.category.toUpperCase()}] ${errorInfo.code}:`, {
      userMessage: errorInfo.userMessage,
      technicalMessage: errorInfo.technicalMessage,
      originalError: error,
      context,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Get user-friendly error message
   */
  static getUserMessage(error: any): string {
    const errorInfo = this.categorizeError(error);
    return errorInfo.userMessage;
  }

  /**
   * Get technical error message for debugging
   */
  static getTechnicalMessage(error: any): string {
    const errorInfo = this.categorizeError(error);
    return errorInfo.technicalMessage;
  }

  /**
   * Check if error is actionable
   */
  static isActionable(error: any): boolean {
    const errorInfo = this.categorizeError(error);
    return errorInfo.actionable;
  }

  /**
   * Get error category
   */
  static getCategory(error: any): ErrorCategory {
    const errorInfo = this.categorizeError(error);
    return errorInfo.category;
  }
}

/**
 * Retry wrapper for async functions
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  config: Partial<RetryConfig> = {}
): Promise<T> {
  const finalConfig = { 
    maxRetries: 3,
    baseDelay: 1000,
    maxDelay: 10000,
    backoffMultiplier: 2,
    ...config 
  };
  let lastError: any;

  for (let attempt = 0; attempt <= finalConfig.maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      
      if (!ErrorHandler.shouldRetry(error, attempt, finalConfig)) {
        throw error;
      }

      if (attempt < finalConfig.maxRetries) {
        const delay = ErrorHandler.calculateRetryDelay(attempt, finalConfig);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError;
}

/**
 * Error boundary hook for React components
 */
export function useErrorHandler() {
  const handleError = (error: any, context?: string) => {
    ErrorHandler.logError(error, context);
    return ErrorHandler.getUserMessage(error);
  };

  const retryOperation = async <T>(
    operation: () => Promise<T>,
    config: Partial<RetryConfig> = {}
  ): Promise<T> => {
    try {
      return await withRetry(operation, config);
    } catch (error) {
      handleError(error, 'retryOperation');
      throw error;
    }
  };

  return {
    handleError,
    retryOperation,
    categorizeError: ErrorHandler.categorizeError,
    getUserMessage: ErrorHandler.getUserMessage,
    isActionable: ErrorHandler.isActionable,
    getCategory: ErrorHandler.getCategory
  };
} 